import { useEffect, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import {
  Sparkles, ExternalLink, Bookmark, MapPin, Building2, Info,
  ChevronRight, Target, TrendingUp, Filter as FilterIcon,
  Wand2, Copy, RefreshCw, Download, ChevronDown, ChevronUp, Loader2, Mail, MessageSquare, FileText, AtSign, ListChecks, Search
} from "lucide-react";

const STAGES = ["Matched", "Interested", "Applied", "Interview", "Follow-up", "Offer", "Rejected"];

const INVALID_HOSTS = ["example.com", "example.org", "example.net", "localhost", "127.0.0.1", "test.com", "dummy.com", "placeholder.com", "foo.com", "bar.com"];
function isValidApplyUrl(url) {
  if (!url || typeof url !== "string") return false;
  const u = url.trim().toLowerCase();
  if (!(u.startsWith("http://") || u.startsWith("https://"))) return false;
  for (const bad of INVALID_HOSTS) {
    if (u.includes(`//${bad}`) || u.includes(`//www.${bad}`)) return false;
  }
  if (u.includes("placeholder") || u.includes("dummy")) return false;
  return true;
}

export default function JobAgent() {
  const [jobs, setJobs] = useState([]);
  const [profileStrength, setProfileStrength] = useState(0);
  const [profilePresent, setProfilePresent] = useState(false);
  const [totalJobsInSystem, setTotalJobsInSystem] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searching, setSearching] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState({});
  const [countryFilter, setCountryFilter] = useState("All");
  const [seniorityFilter, setSeniorityFilter] = useState("All");
  const [minScore, setMinScore] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [matches, saved] = await Promise.all([
        api.get("/job-agent/matches?limit=30"),
        api.get("/job-agent/saved"),
      ]);
      setJobs(matches.data?.jobs || []);
      setProfileStrength(matches.data?.strength || 0);
      setProfilePresent(!!matches.data?.profile_present);
      setTotalJobsInSystem(matches.data?.total_jobs_in_system ?? null);
      const idxMap = {};
      for (const list of Object.values(saved.data?.grouped || {})) {
        for (const s of list) idxMap[s.job_id] = s.stage;
      }
      setSavedIds(idxMap);
    } catch {
      toast.error("Could not load matches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().then(() => {
      setTimeout(() => {
        setJobs((current) => {
          if (current.length === 0) searchLive({ force: false }).catch(() => {});
          return current;
        });
      }, 300);
    });
  }, []);

  const searchLive = async (opts = {}) => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchKeyword.trim()) params.set("keyword", searchKeyword.trim());
      if (opts.force) params.set("force_refresh", "true");
      params.set("limit", "30");
      const r = await api.post(`/job-agent/search?${params.toString()}`);
      setJobs(r.data?.jobs || []);
      setProfileStrength(r.data?.strength || 0);
      setProfilePresent(!!r.data?.profile_present);
      setTotalJobsInSystem(r.data?.total_jobs_in_system ?? null);
      setLastFetchedAt(r.data?.fetched_at || null);
      const hit = r.data?.cache_hit ? " (cached)" : "";
      toast.success(`Found ${r.data?.jobs?.length || 0} live jobs${hit}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Live search failed");
    } finally { setSearching(false); }
  };

  const broadenSearch = () => { setSearchKeyword(""); searchLive({ force: true }); };

  const saveToPipeline = async (job, stage = "Interested") => {
    if (!STAGES.includes(stage)) {
      toast.error(`Invalid stage: ${stage}`);
      return;
    }
    setSavingId(job.job_id);
    try {
      const r = await api.post("/job-agent/save", { job_id: job.job_id, stage });
      if (!r?.data?.ok) {
        toast.error("Could not save. Try again.");
      } else {
        setSavedIds((m) => ({ ...m, [job.job_id]: stage }));
        toast.success(`Saved to ${stage}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSavingId(null);
    }
  };

  const countries = ["All", ...Array.from(new Set(jobs.map((j) => j.country))).sort()];
  const seniorities = ["All", "entry", "mid", "senior"];

  const filtered = jobs.filter((j) => {
    if (countryFilter !== "All" && j.country !== countryFilter) return false;
    if (seniorityFilter !== "All" && j.seniority !== seniorityFilter) return false;
    if ((j.match_score || 0) < minScore) return false;
    return true;
  });

  return (
    <div className="space-y-8" data-testid="job-agent-root">
      <div className="fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="chip mb-3"><Sparkles size={12} /> Job Agent</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <span className="text-neon">Your Job Search Agent</span>
          </h1>
          <p className="mt-3 text-slate-400 text-lg max-w-2xl leading-relaxed">
            Matched jobs based on your CV, skills, and career profile.
          </p>
        </div>
        <Link
          to="/pipeline"
          data-testid="go-pipeline"
          className="btn-neon inline-flex items-center gap-2"
        >
          Open pipeline <ChevronRight size={16} />
        </Link>
      </div>

      {/* Profile hint */}
      {!profilePresent || profileStrength < 60 ? (
        <div className="glass p-4 flex items-start gap-3" data-testid="profile-hint">
          <Info size={18} className="mt-0.5 text-[#FFD166] shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-white">
              {profilePresent ? `Profile strength ${profileStrength}%. ` : "No career profile yet. "}
              Sharpen it to unlock better matches.
            </div>
            <Link to="/career-profile" className="text-sm text-[#80FFEA] underline font-semibold mt-1 inline-block">
              Update career profile →
            </Link>
          </div>
        </div>
      ) : null}

      {/* Live search bar */}
      <div className="glass p-4 flex flex-wrap items-center gap-3" data-testid="live-search-bar">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] rounded-xl bg-white/5 border border-white/10 px-3 py-2 focus-within:border-[#00E5FF]">
          <Search size={14} className="text-slate-500" />
          <input
            data-testid="live-search-input"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") searchLive({ force: true }); }}
            placeholder="Search job title or keyword (e.g. Frontend, Python)…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
          />
        </div>
        <button
          data-testid="live-search-btn"
          onClick={() => searchLive({ force: true })}
          disabled={searching}
          className="btn-neon inline-flex items-center gap-2 !py-2.5 disabled:opacity-60"
        >
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {searching ? "Searching…" : "Search Live Jobs"}
        </button>
        <button
          data-testid="live-search-refresh"
          onClick={() => searchLive({ force: true })}
          disabled={searching}
          title="Refresh live results"
          className="btn-glass inline-flex items-center gap-2 !py-2.5"
        >
          <RefreshCw size={14} />
        </button>
        {lastFetchedAt && (
          <div className="text-[10px] text-slate-500 basis-full">
            Last fetched: {new Date(lastFetchedAt).toLocaleString()}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="glass p-4 flex flex-wrap items-center gap-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2"><FilterIcon size={12} /> Filter</div>
        <select
          data-testid="filter-country"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
        >
          {countries.map((c) => <option key={c} value={c} className="bg-[#0A0A12]">{c === "All" ? "All countries" : c}</option>)}
        </select>
        <select
          data-testid="filter-seniority"
          value={seniorityFilter}
          onChange={(e) => setSeniorityFilter(e.target.value)}
          className="rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
        >
          {seniorities.map((s) => <option key={s} value={s} className="bg-[#0A0A12]">{s === "All" ? "All seniorities" : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <span>Min match</span>
          <input
            data-testid="filter-min-score"
            type="range"
            min={0}
            max={100}
            step={5}
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="w-32 accent-[#9D00FF]"
          />
          <span className="font-bold w-10 text-[#80FFEA]">{minScore}%</span>
        </div>
        <div className="ml-auto text-xs text-slate-500" data-testid="job-count">
          {loading ? "Loading…" : `${filtered.length} of ${jobs.length} jobs`}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-slate-500" data-testid="jobs-loading">Loading matches…</div>
      ) : jobs.length === 0 ? (
        <div className="glass p-10 text-center" data-testid="jobs-empty">
          <div className="font-display font-bold text-2xl text-white">No live job matches found yet</div>
          <div className="text-slate-400 mt-3 max-w-lg mx-auto">
            We could not find verified live jobs matching your profile right now. Try updating your Career Profile, changing your location, or broadening your target roles.
          </div>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/career-profile" data-testid="empty-update-profile" className="btn-neon inline-flex items-center gap-2">
              Update Career Profile
            </Link>
            <button
              data-testid="empty-search-again"
              onClick={() => searchLive({ force: true })}
              disabled={searching}
              className="btn-glass inline-flex items-center gap-2 disabled:opacity-60"
            >
              <RefreshCw size={14} /> {searching ? "Searching…" : "Search Again"}
            </button>
            <button
              data-testid="empty-broaden"
              onClick={broadenSearch}
              disabled={searching}
              className="btn-glass inline-flex items-center gap-2 disabled:opacity-60"
            >
              Broaden Search
            </button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass p-10 text-center" data-testid="jobs-empty-filtered">
          <div className="font-display font-bold text-xl text-white">No jobs match your filters.</div>
          <div className="text-slate-400 mt-2">Try loosening filters or updating your career profile.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((j) => (
            <JobCard
              key={j.job_id}
              job={j}
              savedStage={savedIds[j.job_id]}
              saving={savingId === j.job_id}
              onSave={(stage) => saveToPipeline(j, stage)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function JobCard({ job, savedStage, saving, onSave }) {
  const score = job.match_score || 0;
  const scoreColor = score >= 80 ? "#00E5FF" : score >= 60 ? "#9D00FF" : "#FF007F";
  const scoreLabel = score >= 80 ? "Strong match" : score >= 60 ? "Good match" : "Low match";
  const [showStages, setShowStages] = useState(false);
  const applyOk = isValidApplyUrl(job.apply_url);
  const dash = Math.max(0, Math.min(100, score));
  return (
    <div className="glass p-5 hover-lift flex flex-col gap-3 relative overflow-hidden" data-testid={`job-card-${job.job_id}`}>
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-15 blur-3xl" style={{ background: scoreColor }} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {job.industry && (
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#80FFEA]">{job.industry}</span>
            )}
            {job.source && (
              <span className="text-[10px] font-semibold text-slate-500">· {job.source}</span>
            )}
          </div>
          <div className="font-display font-bold text-lg mt-1 leading-tight text-white" data-testid="job-title">{job.title}</div>
          <div className="text-sm text-slate-300 mt-0.5 flex items-center gap-1"><Building2 size={12} /> {job.company}</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={12} /> {job.location}</div>
          {job.posted_at && (
            <div className="text-[11px] text-slate-500 mt-0.5">Posted {job.posted_at}</div>
          )}
        </div>
        {/* Circular match score */}
        <div className="relative w-16 h-16 shrink-0" data-testid="match-score">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" className="ring-track" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={scoreColor} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${dash}, 100`} style={{ filter: `drop-shadow(0 0 6px ${scoreColor}aa)` }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="font-display font-black text-lg leading-none" style={{ color: scoreColor }}>{score}</div>
          </div>
        </div>
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: scoreColor }}>{scoreLabel}</div>

      {job.salary_range && (
        <div className="text-xs text-slate-400"><span className="font-semibold text-white">Salary:</span> {job.salary_range}</div>
      )}

      {job.why_matches?.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Why it matches</div>
          <ul className="text-xs text-slate-300 space-y-1">
            {job.why_matches.slice(0, 3).map((r, i) => (
              <li key={i} className="flex items-start gap-1.5"><TrendingUp size={12} className="mt-0.5 text-[#80FFEA] shrink-0" /> {r}</li>
            ))}
          </ul>
        </div>
      )}

      {job.missing_skills?.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Skills to sharpen</div>
          <div className="flex flex-wrap gap-1.5">
            {job.missing_skills.slice(0, 4).map((s, i) => (
              <span key={i} className="text-[10px] font-semibold rounded-full bg-[rgba(255,209,102,0.12)] text-[#FFD166] border border-[rgba(255,209,102,0.3)] px-2 py-0.5">{s}</span>
            ))}
          </div>
        </div>
      )}

      {job.next_step && (
        <div className="text-xs text-slate-300 bg-white/[0.03] border border-white/5 rounded-xl p-2.5 flex items-start gap-2">
          <Target size={12} className="mt-0.5 shrink-0 text-[#FFD166]" /> {job.next_step}
        </div>
      )}

      <div className="mt-auto flex items-center gap-2 pt-2">
        {savedStage ? (
          <div className="flex-1 text-xs font-bold text-[#80FFEA] rounded-xl bg-[rgba(128,255,234,0.1)] border border-[rgba(128,255,234,0.25)] px-3 py-2 inline-flex items-center gap-1" data-testid="saved-indicator">
            <Bookmark size={12} /> In pipeline · {savedStage}
          </div>
        ) : (
          <div className="relative flex-1">
            <button
              data-testid={`save-btn-${job.job_id}`}
              onClick={() => setShowStages((s) => !s)}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl btn-neon text-xs py-2 disabled:opacity-60"
            >
              <Bookmark size={12} /> {saving ? "Saving…" : "Save to pipeline"}
            </button>
            {showStages && (
              <div className="absolute z-10 top-full left-0 right-0 mt-1 rounded-xl overflow-hidden border border-white/10"
                style={{ background: "rgba(10,10,18,0.95)", backdropFilter: "blur(20px)" }}>
                {STAGES.map((s) => (
                  <button
                    key={s}
                    data-testid={`save-stage-${s}`}
                    onClick={() => { setShowStages(false); onSave(s); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 font-semibold text-slate-300 hover:text-white"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {applyOk && (
          <a
            href={job.apply_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`apply-link-${job.job_id}`}
            className="btn-glass inline-flex items-center gap-1 text-xs !py-2 !px-3"
          >
            <ExternalLink size={12} /> Apply
          </a>
        )}
      </div>

      <ApplicationPackSection job={job} />
    </div>
  );
}

function ApplicationPackSection({ job }) {
  const [open, setOpen] = useState(false);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchExisting = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/job-agent/pack/${job.job_id}`);
      setPack(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setPack(null);
      else setError(e?.response?.data?.detail || "Could not load");
    } finally { setLoading(false); }
  };

  const generate = async () => {
    setGenerating(true); setError(null);
    try {
      const r = await api.post(`/job-agent/pack/${job.job_id}`);
      setPack(r.data);
      toast.success("Application pack ready");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Generation failed";
      setError(detail);
      toast.error(detail);
    } finally { setGenerating(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate the pack? This will overwrite the current one.")) return;
    await generate();
  };

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("cavilo_token");
      const res = await fetch(`${API_BASE}/job-agent/pack/${job.job_id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = `${job.company}-${job.title}`.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      a.download = `cavilow_pack_${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally { setDownloading(false); }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loading && !generating) fetchExisting();
  };

  return (
    <div className="border-t border-white/10 pt-3 mt-1" data-testid={`pack-section-${job.job_id}`}>
      <button
        data-testid={`pack-toggle-${job.job_id}`}
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 text-xs font-bold text-white transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <Wand2 size={13} className="text-[#9D00FF]" /> Application Pack
          {pack && <span className="text-[10px] font-bold rounded-full bg-[#80FFEA]/15 text-[#80FFEA] px-2 py-0.5 border border-[#80FFEA]/30">Ready</span>}
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`pack-body-${job.job_id}`}>
          {loading && <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}
          {!loading && !pack && !generating && (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-4 text-center">
              <div className="text-sm font-semibold text-white">One-tap kit for {job.title}</div>
              <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                We&apos;ll draft a tailored summary, sharpen 6 bullets, cover letter, LinkedIn note, HR email and 6 interview questions — all in one go.
              </p>
              <button
                data-testid={`pack-generate-${job.job_id}`}
                onClick={generate}
                className="mt-3 btn-neon inline-flex items-center gap-2 !py-2 !px-4 text-xs"
              >
                <Wand2 size={12} /> Generate Application Pack
              </button>
              <div className="text-[10px] text-slate-500 mt-2">Uses your latest uploaded resume as the source of truth.</div>
            </div>
          )}

          {generating && (
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center" data-testid={`pack-generating-${job.job_id}`}>
              <Loader2 size={22} className="mx-auto animate-spin text-[#00E5FF]" />
              <div className="text-xs font-bold mt-2 text-white">Crafting your pack…</div>
              <div className="text-[10px] text-slate-400 mt-1">Running 6 focused LLM passes in parallel. This usually takes 20-40 seconds.</div>
            </div>
          )}

          {error && !generating && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs p-3">
              {error}
            </div>
          )}

          {pack && !generating && (
            <>
              <div className="flex items-center justify-end gap-2">
                <button
                  data-testid={`pack-download-${job.job_id}`}
                  onClick={download}
                  disabled={downloading}
                  className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 text-slate-200 px-2.5 py-1.5 text-[11px] font-bold hover:bg-white/10 disabled:opacity-60"
                >
                  <Download size={11} /> {downloading ? "…" : "Download PDF"}
                </button>
                <button
                  data-testid={`pack-regenerate-${job.job_id}`}
                  onClick={regenerate}
                  className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 text-slate-200 px-2.5 py-1.5 text-[11px] font-bold hover:bg-white/10"
                >
                  <RefreshCw size={11} /> Regenerate
                </button>
              </div>
              <PackAsset title="Tailored summary" icon={FileText} testId={`pack-summary-${job.job_id}`}
                value={pack.pack?.tailored_summary?.summary || ""} />
              <PackBullets bullets={pack.pack?.bullet_fixes?.bullets || []} testIdBase={`pack-bullets-${job.job_id}`} />
              <PackAsset title="Cover letter" icon={FileText} testId={`pack-cover-${job.job_id}`}
                value={pack.pack?.cover_letter?.cover_letter || ""} preformatted />
              <PackAsset title="LinkedIn note" icon={AtSign} testId={`pack-linkedin-${job.job_id}`}
                value={pack.pack?.linkedin_note?.linkedin_note || ""} />
              <PackHrEmail hr={pack.pack?.hr_email || {}} testIdBase={`pack-hr-${job.job_id}`} />
              <PackInterview questions={pack.pack?.interview_prep?.questions || []} testIdBase={`pack-iv-${job.job_id}`} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function copyValue(text) {
  try { navigator.clipboard.writeText(text || ""); toast.success("Copied"); }
  catch { toast.error("Copy failed"); }
}

function PackAsset({ title, icon: Icon, value, testId, preformatted }) {
  if (!value) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1"><Icon size={11} /> {title}</div>
        <button
          onClick={() => copyValue(value)}
          className="inline-flex items-center gap-1 rounded-lg text-[11px] font-bold text-slate-300 hover:bg-white/10 px-2 py-1"
        >
          <Copy size={11} /> Copy
        </button>
      </div>
      <div className={`text-xs text-slate-200 leading-relaxed ${preformatted ? "whitespace-pre-wrap" : ""}`}>{value}</div>
    </div>
  );
}

function PackBullets({ bullets, testIdBase }) {
  if (!bullets?.length) return null;
  const all = bullets.map((b, i) => `• Original: ${b.original || ""}\n  Rewrite: ${b.rewrite || ""}`).join("\n\n");
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={testIdBase}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1"><ListChecks size={11} /> Bullet fixes ({bullets.length})</div>
        <button onClick={() => copyValue(all)} className="inline-flex items-center gap-1 rounded-lg text-[11px] font-bold text-slate-300 hover:bg-white/10 px-2 py-1">
          <Copy size={11} /> Copy all
        </button>
      </div>
      <ol className="space-y-2 list-decimal list-inside">
        {bullets.map((b, i) => (
          <li key={i} className="text-xs text-slate-200 space-y-1" data-testid={`${testIdBase}-item-${i}`}>
            <div className="text-slate-400"><span className="font-semibold text-slate-300">Original:</span> {b.original || "—"}</div>
            <div><span className="font-semibold text-[#80FFEA]">Rewrite:</span> {b.rewrite || "—"}</div>
            {b.rationale && <div className="text-[11px] text-slate-500 italic">Why: {b.rationale}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function PackHrEmail({ hr, testIdBase }) {
  if (!hr?.subject && !hr?.body) return null;
  const combined = `Subject: ${hr.subject || ""}\n\n${hr.body || ""}`;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={testIdBase}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1"><Mail size={11} /> HR / Recruiter email</div>
        <button onClick={() => copyValue(combined)} className="inline-flex items-center gap-1 rounded-lg text-[11px] font-bold text-slate-300 hover:bg-white/10 px-2 py-1">
          <Copy size={11} /> Copy
        </button>
      </div>
      {hr.subject && <div className="text-xs font-semibold text-white mb-1">Subject: {hr.subject}</div>}
      <div className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed">{hr.body}</div>
    </div>
  );
}

function PackInterview({ questions, testIdBase }) {
  if (!questions?.length) return null;
  const all = questions.map((q, i) => `Q${i + 1} (${q.category || ""}): ${q.question}\n${q.answer_outline || ""}`).join("\n\n");
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={testIdBase}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1"><MessageSquare size={11} /> Interview prep ({questions.length})</div>
        <button onClick={() => copyValue(all)} className="inline-flex items-center gap-1 rounded-lg text-[11px] font-bold text-slate-300 hover:bg-white/10 px-2 py-1">
          <Copy size={11} /> Copy all
        </button>
      </div>
      <div className="space-y-2">
        {questions.map((q, i) => (
          <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-2" data-testid={`${testIdBase}-item-${i}`}>
            <div className="text-xs font-semibold text-white">Q{i + 1} · <span className="text-slate-400 font-normal capitalize">{q.category || "general"}</span></div>
            <div className="text-xs text-slate-200 mt-0.5">{q.question}</div>
            <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">{q.answer_outline}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
